import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const PRIMARY = 'var(--color-primary)';
const BORDER = 'var(--color-border)';
const TEXT_MUTED = 'var(--color-text-muted)';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px' }}>{title}</h2>
    <div style={{ fontSize: 14, color: 'var(--color-text)', lineHeight: 1.7 }}>{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: 'var(--color-background)', minHeight: '100vh', padding: '24px 16px' }}>
      <Helmet>
        <title>Privacy Policy | Phrenos</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: PRIMARY, fontSize: 13, fontWeight: 600, padding: 0,
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          ← Back
        </button>

        <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--color-text)', margin: '0 0 4px' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: 12, color: TEXT_MUTED, margin: '0 0 32px' }}>
          Last updated: March 15, 2026
        </p>

        <div style={{
          backgroundColor: 'var(--color-surface)',
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
          padding: '28px 24px',
        }}>
          <Section title="Overview">
            <p style={{ margin: 0 }}>
              Phrenos ("we", "our", or "us") is a vocabulary and quantitative study tool for GRE, SAT, and GMAT preparation.
              This Privacy Policy explains what information we collect, how we use it, and your rights with respect to that information.
              By using Phrenos at <strong>phrenos.app</strong>, you agree to this policy.
            </p>
          </Section>

          <Section title="Information We Collect">
            <p style={{ margin: '0 0 10px' }}><strong>Account information.</strong> When you sign up, we collect your email address via Supabase Auth. We do not collect your name, phone number, or payment information — Phrenos is free.</p>
            <p style={{ margin: '0 0 10px' }}><strong>Study progress.</strong> We store your flashcard position, synonym completion records, and any reported word issues in our database (Supabase) so your progress persists across devices and sessions.</p>
            <p style={{ margin: '0 0 10px' }}><strong>Usage data.</strong> We use Vercel Analytics to collect anonymous, aggregated usage statistics (page views, referrer). No personal identifiers are attached to this data.</p>
            <p style={{ margin: 0 }}><strong>Local storage.</strong> Your browser stores session timing and position data locally to improve performance. This data never leaves your device except when synced to your account.</p>
          </Section>

          <Section title="How We Use Your Information">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>To authenticate you and restore your study progress across sessions</li>
              <li>To sync your progress across multiple devices</li>
              <li>To understand aggregate usage patterns and improve the app</li>
              <li>To respond to bug reports or issues you submit</li>
            </ul>
          </Section>

          <Section title="Data Sharing">
            <p style={{ margin: 0 }}>
              We do not sell, rent, or share your personal information with third parties for marketing purposes.
              Your data is processed by the following sub-processors as necessary to operate the service:
            </p>
            <ul style={{ margin: '10px 0 0', paddingLeft: 20 }}>
              <li><strong>Supabase</strong> — database, authentication, and storage</li>
              <li><strong>Vercel</strong> — hosting and anonymous analytics</li>
            </ul>
          </Section>

          <Section title="Data Retention">
            <p style={{ margin: 0 }}>
              We retain your account and progress data for as long as your account is active.
              If you delete your account or request deletion, we will remove your personal data within 30 days.
              Anonymous analytics data may be retained indefinitely as it cannot be linked to you.
            </p>
          </Section>

          <Section title="Your Rights">
            <p style={{ margin: '0 0 8px' }}>You have the right to:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and associated data</li>
              <li>Export your data in a portable format</li>
            </ul>
            <p style={{ margin: '10px 0 0' }}>
              To exercise these rights, email us at <strong>support@phrenos.app</strong>.
            </p>
          </Section>

          <Section title="Security">
            <p style={{ margin: 0 }}>
              We use industry-standard security practices including encrypted connections (HTTPS) and
              Supabase's row-level security policies to protect your data. However, no system is completely
              secure — please use a strong, unique password for your account.
            </p>
          </Section>

          <Section title="Children's Privacy">
            <p style={{ margin: 0 }}>
              Phrenos is not directed at children under 13. We do not knowingly collect personal information
              from children under 13. If you believe a child has provided us with personal information,
              please contact us and we will delete it promptly.
            </p>
          </Section>

          <Section title="Changes to This Policy">
            <p style={{ margin: 0 }}>
              We may update this Privacy Policy from time to time. We will notify registered users of
              material changes by updating the "Last updated" date above. Continued use of Phrenos after
              changes constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="Contact">
            <p style={{ margin: 0 }}>
              Questions about this Privacy Policy? Contact us at <strong>support@phrenos.app</strong>.
            </p>
          </Section>
        </div>

        <p style={{ fontSize: 11, color: TEXT_MUTED, textAlign: 'center', marginTop: 24 }}>
          © 2026 Phrenos. Not affiliated with ETS, College Board, GMAC, or LSAC.
        </p>
      </div>
    </div>
  );
}
